'use client';

import {useEffect, useRef} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {useI18n} from '@udemy-v2/i18n';
import {useSmartBarStore, fetchSmartBarData} from '@udemy-v2/react-smart-bar';

/**
 * Workaround for a timing bug in @udemy-v2/react-smart-bar.
 *
 * The SmartBarStoreProvider's built-in fetch effect checks page.isLoading
 * before fetching. On ISR pages, the effect runs before bootstrap completes
 * (when isLoading is still true) and bails permanently — it doesn't re-run
 * because commonData is a stable MobX reference.
 *
 * This component watches page.isLoading via useCommonData() and triggers
 * the smart bar fetch after bootstrap completes. It lives inside the
 * provider tree as a child — no changes to core-providers.tsx needed.
 */
export function SmartBarActivator() {
    const commonData = useCommonData();
    const {gettext} = useI18n();
    const store = useSmartBarStore();
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (fetchedRef.current) return;
        if (commonData.page?.isLoading) return;
        if (!store.showSmartBar) return;
        if (store.data) return;

        // Prevent duplicate concurrent fetches
        fetchedRef.current = true;

        const doFetch = async () => {
            try {
                const params = Object.fromEntries(new URLSearchParams(window.location.search));
                const result = await fetchSmartBarData({
                    commonData,
                    forceNoticeType: 'smart_bar',
                    params,
                    gettext,
                });
                store.setData({
                    data: result.uiModel ?? undefined,
                    isFallback: result.isFallbackNotice,
                });
            } catch {
                // Fetch failed — reset so the effect can retry on next trigger
                fetchedRef.current = false;
            }
        };

        doFetch();
    }, [commonData, commonData.page?.isLoading, store, store.showSmartBar, store.data, gettext]);

    return null;
}
